<script lang="ts">
import z from 'zod';
import isNumeric from 'validator/lib/isNumeric';
import { removeCommas } from '@/utilities/Number';
import currency from 'currency.js';

export default {
  name: 'CyberForm',
};

export interface Form {
  companyName: string;
  countryCode: string;
  sicCode: string;
  annualRevenue: string;
  revenueEffective?: DateValue;
  actualLimits: string;
  actualRetention: string;
  targetLimits: string;
  targetRetention: string;
}

const AnnualRevenueExposureMetricID = 1;

const numericString = z
  .string()
  .trim()
  .refine((val) => val === '' || isNumeric(val), {
    message: 'Must be a number',
  })
  .default('');
</script>

<script setup lang="ts">
import DatePickerInput from '@/components/v2/DatePickerInput.vue';
import SICCodesAutocompleteInput from '@/components/SICCodesAutocompleteInput.vue';
import { cyberCubeService, accountService } from '@/services/api';
import { ExposureValue } from '@/types/AccountExposure';
import { CalendarDate, parseAbsolute, parseDate, type DateValue } from '@internationalized/date';
import { computed, ref, WritableComputedOptions } from 'vue';
import TextField from '@/components/v2/TextField.vue';
import { onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { SelectOption } from '@/types/components/Select';
import Combobox from '@/components/v2/Combobox.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Spinner from '@/components/v2/Spinner.vue';
import { Account } from '@/types/Account.js';
import { CompanyActualLimitsAndRetention, ExposureValueRequest } from '@/types/CyberCube.js';
import { Commit } from '@/store/index.js';
import { StoreMutationType } from '@/types/StoreMutationType.js';
import { LucideBadgeCheck } from '@lucide/vue';

const route = useRoute();
const router = useRouter();

const props = defineProps<{
  isEditMode: boolean;
}>();

// -----
// refs
// -----
const form = ref<Form>({} as Form);
const loading = ref<boolean>(false);
const clients = ref<SelectOption[]>([]);
const selectedCompanyId = ref<string>('');
const account = ref<Account>({} as Account);
const accountsWithExistingModels = ref<number[]>([]);
const snackbar = ref<boolean>(false);

// ---------
// computed
// ---------
const title = computed(() => {
  if (props.isEditMode) {
    return 'Update CyberCube Account';
  }
  return 'Create CyberCube Account';
});

const computedAnnualRevenue = computed(createComputedCurrencyField('annualRevenue'));
const computedTargetLimits = computed(createComputedCurrencyField('targetLimits'));
const computedTargetRetention = computed(createComputedCurrencyField('targetRetention'));
const computedActualLimits = computed(createComputedCurrencyField('actualLimits'));
const computedActualRetention = computed(createComputedCurrencyField('actualRetention'));

const revenueEffectiveString = computed({
  get: () => form.value.revenueEffective?.toString() ?? '',
  set: (val: string) => {
    if (!val || val.trim() === '') {
      form.value.revenueEffective = undefined;
    } else {
      try {
        form.value.revenueEffective = parseDate(val) as DateValue;
      } catch {
        form.value.revenueEffective = undefined;
      }
    }
  },
});

const accountId = computed(() => {
  return (route.params.id as string) || null;
});

// ----------
// functions
// ----------
function createComputedCurrencyField(field: string): WritableComputedOptions<string> {
  return {
    get: () => {
      const amount = form.value[field];
      try {
        numericString.parse(removeCommas(amount));
        return toFormattedCurrency(amount);
      } catch (e) {
        return amount;
      }
    },
    set: (value: string) => {
      try {
        numericString.parse(removeCommas(value));
        form.value[field] = toCurrency(value);
      } catch (e) {
        form.value[field] = value;
      }
    },
  };
}

function toFormattedCurrency(amount: string): string {
  if (!amount) {
    return '';
  }
  return currency(amount, { fromCents: false, precision: 0, pattern: '$ #' }).format();
}

function toCurrency(amount: string): string {
  if (amount === '') {
    return '';
  }
  return currency(amount, { fromCents: false }).value.toString();
}

async function refreshData() {
  loading.value = true;
  await populateClients();

  // if coming from account analytics or editing an existing cyber model, can prepopulate the form
  if (account.value && account.value.accountID) {
    const client = clients.value.find((client) => client.value === account.value.accountID?.toString());
    if (client) {
      await handleAccountSelect(client);
      // this will also set default values for the form
    }

    loading.value = false;
  }

  loading.value = false;
}

async function populateClients() {
  const accountList = await accountService.list();
  clients.value = accountList.map((account) => {
    return {
      label: account.accountName,
      value: account.accountID.toString(),
    };
  });

  // determine which clients already have a CyberCube model
  accountsWithExistingModels.value = await cyberCubeService.getAccountsWithExistingModels();
}

async function setDefaults() {
  if (account.value.accountID) {
    let exposureValues: ExposureValue[] = [];
    try {
      exposureValues = await accountService.getExposures(account.value.accountID);
    } catch (error: any) {
      console.error('Error fetching exposures', error);
    }

    let actualLimitsAndRetention: CompanyActualLimitsAndRetention = {
      actualLimits: null,
      actualRetention: null,
    };
    try {
      actualLimitsAndRetention = await cyberCubeService.getCompanyActualLimitsAndRetention(account.value.accountID);
    } catch (error) {
      console.error('Error fetching actual limits and retention for account', error);
    }

    // defaults
    if (!accountsWithExistingModels.value.includes(account.value.accountID)) {
      // creating a new cybercube account
      form.value = {
        companyName: account.value.accountName,
        countryCode: 'US',
        sicCode: account.value.sIC || '',
        annualRevenue: getAnnualRevenue(exposureValues),
        revenueEffective: getRevenueEffectiveDate(exposureValues),
        targetLimits: '',
        targetRetention: '',
        actualLimits: (actualLimitsAndRetention.actualLimits || '').toString(),
        actualRetention: (actualLimitsAndRetention.actualRetention || '').toString(),
      };
    } else if (accountsWithExistingModels.value.includes(account.value.accountID)) {
      if (!props.isEditMode) {
        // if the user did enter this page through the 'edit' flow but chose to create a cyber model for an account
        // with an existing model, warn them this action will update the existing model
        snackbar.value = true;
      }

      // editing an existing cybercube account
      if (!account.value.cyberCubeID) {
        throw new Error('CyberCube Id is required for update');
      }

      loading.value = true;
      try {
        const response = await cyberCubeService.getCompanyExposureAccount(account.value.accountID, account.value.cyberCubeID);
        form.value = {
          companyName: response.companyName,
          countryCode: response.countryCode,
          sicCode: response.sic4 || account.value.sIC || '',
          annualRevenue: response.revenue.toString() || getAnnualRevenue(exposureValues),
          revenueEffective: getRevenueEffectiveDate(exposureValues),
          targetLimits: (response.targetLimits || '').toString(),
          targetRetention: (response.targetDeductible || '').toString(),
          actualLimits: (response.actualLimits || actualLimitsAndRetention.actualLimits || '').toString(),
          actualRetention: (response.actualDeductible || actualLimitsAndRetention.actualRetention || '').toString(),
        };
      } catch (e: any) {
        if (e.response?.status === 429 || e.cause?.status === 429) {
          Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'CyberCube is not available', color: 'error' });
        }
      }
      loading.value = false;
    }
  }
}

async function handleAccountSelect(selectedAccount: SelectOption) {
  selectedCompanyId.value = selectedAccount.value;
  form.value.companyName = selectedAccount.label;
  account.value = await accountService.findById(selectedAccount.value);

  await setDefaults();
}

async function handleSubmit() {
  // TODO: validate form
  if (form.value) {
    try {
      loading.value = true;

      if (accountsWithExistingModels.value.includes(account.value.accountID)) {
        // update scenario
        if (!account.value.cyberCubeID) {
          throw new Error('CyberCube Id is required for update');
        }

        await cyberCubeService.updateCompanyExposureAccount(account.value.accountID, account.value.cyberCubeID, toRequest(form.value as Form));
      } else {
        // create scenario
        await cyberCubeService.createAccount(account.value.accountID, toRequest(form.value as Form));
      }

      loading.value = false;
    } catch (error: any) {
      // CyberCube rate limit hit
      if (error.response?.status === 429 || error.cause?.status === 429) {
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'CyberCube is not available', color: 'error' });
      }

      console.error(error);
      loading.value = false;
    }
  }
  const categoryId = route.params.categoryId as string;

  const saveRoute = route.path.includes('/account')
    ? `/account/${accountId.value}/analytics/category/${categoryId}/cyber/view`
    : `/analytics/category/${categoryId}/cyber/${account.value.accountID}/view`;
  return router.push(saveRoute);
}

function getRevenueEffectiveDate(exposureValues: ExposureValue[]): DateValue | undefined {
  if (exposureValues.length === 0) {
    return undefined;
  }

  const revenueEffectiveDate = getLatestExposureByMetric(AnnualRevenueExposureMetricID, exposureValues)?.effectiveDate;

  if (!revenueEffectiveDate) {
    return undefined;
  }

  const zonedDateTime = parseAbsolute(revenueEffectiveDate, 'UTC');
  return new CalendarDate(zonedDateTime.calendar, zonedDateTime.year, zonedDateTime.month, zonedDateTime.day);
}

function getAnnualRevenue(exposureValues: ExposureValue[]): string {
  if (exposureValues.length === 0) {
    return '';
  }

  const annualRevenue = getLatestExposureByMetric(AnnualRevenueExposureMetricID, exposureValues)?.value;
  return annualRevenue ? annualRevenue.toString() : '';
}

function getLatestExposureByMetric(metricId: number, exposureValues: ExposureValue[]): ExposureValue | undefined {
  if (exposureValues.length === 0 || !metricId) {
    return undefined;
  }

  return exposureValues
    .filter((exposureValue) => exposureValue.exposureMetricID === metricId)
    .sort((a, b) => {
      const diff = new Date(b.effectiveDate!).getTime() - new Date(a.effectiveDate!).getTime();
      if (diff === 0) {
        // for matching effective dates, sort by upload timestamp descending
        return new Date(b.uploadTimeStamp).getTime() - new Date(a.uploadTimeStamp).getTime();
      } else {
        return diff;
      }
    })[0];
}

function toRequest(form: Form): Partial<ExposureValueRequest> {
  return {
    companyName: form.companyName,
    countryCode: form.countryCode,
    sic4: form.sicCode,
    revenue: Number(removeCommas(form.annualRevenue)),
    revenueEffectiveDate: form.revenueEffective?.toDate('UTC') ?? null,
    actualLimits: Number(removeCommas(form.actualLimits)),
    actualDeductible: Number(removeCommas(form.actualRetention)),
    targetLimits: Number(removeCommas(form.targetLimits)),
    targetDeductible: Number(removeCommas(form.targetRetention)),
  };
}

// ----------
// lifecycle
// ----------

onMounted(async () => {
  // if coming from account -> analytics
  loading.value = true;

  if (route.params.id) {
    account.value = await accountService.findById(route.params.id as string);
  } else {
    // set defaults for new model
    form.value = {
      companyName: '',
      countryCode: 'US',
      sicCode: '',
      annualRevenue: '',
      revenueEffective: undefined,
      actualLimits: '',
      actualRetention: '',
      targetLimits: '',
      targetRetention: '',
    };
  }

  await refreshData();
});
</script>

<template>
  <div
    v-if="loading"
    class="flex h-[50vh] items-center justify-center">
    <Spinner size="lg" />
  </div>

  <div
    v-else
    class="inner w-full max-w-7xl p-4 text-primary">
    <div class="header">
      <h1 data-testid="form-header">{{ title }}</h1>
    </div>
    <!-- snackbar -->
    <div
      v-if="snackbar"
      class="mb-4 alert flex items-center justify-between gap-4 alert-success">
      <div class="flex items-center gap-4">
        <LucideBadgeCheck
          :size="20"
          class="shrink-0" />
        <div>
          <strong>Cybercube Account Exists</strong>
          <br />
          A Cybercube Account already exists for this company. This form will update the existing account.
        </div>
      </div>
      <AppButton @click="snackbar = false">OK</AppButton>
    </div>
    <!-- form -->
    <form class="relative m-4 grid p-2 lg:grid-cols-[1fr_255px]">
      <div class="flex flex-col gap-10 xl:mr-10">
        <div class="mr-4 flex flex-col gap-4 pr-4">
          <div class="flex flex-col">
            <label
              for="company-name"
              class="pb-1"
              >Company Name</label
            >
            <Combobox
              class="w-full"
              data-testid="company-name"
              :disabled="accountId !== null"
              v-model="selectedCompanyId"
              @selected="handleAccountSelect"
              :options="clients" />
          </div>

          <div class="flex flex-col">
            <TextField
              id="country-code"
              label="Country Code"
              class="w-full"
              v-model="form.countryCode" />
          </div>

          <div class="flex flex-col">
            <label
              for="sic-code"
              class="pb-1"
              >SIC Code</label
            >
            <SICCodesAutocompleteInput
              v-model="form.sicCode"
              large
              class="w-full" />
          </div>

          <div class="flex flex-col">
            <TextField
              id="annual-revenue"
              label="Annual Revenue"
              type="text"
              class="w-full"
              v-model="computedAnnualRevenue" />
          </div>

          <div class="flex flex-col">
            <DatePickerInput
              id="revenue-effective-date"
              data-testid="revenue-effective-date"
              label="Revenue Effective"
              class="w-full"
              placeholder="Select date"
              v-model="revenueEffectiveString" />
          </div>

          <div class="flex flex-col">
            <TextField
              id="actual-limits"
              label="Actual Limits"
              type="text"
              class="w-full"
              v-model="computedActualLimits" />
          </div>
          <div class="flex flex-col">
            <TextField
              id="actual-retention"
              label="Actual Retention"
              type="text"
              class="w-full"
              v-model="computedActualRetention" />
          </div>
          <div class="flex flex-col">
            <TextField
              id="target-limits"
              label="Target Limits"
              type="text"
              class="w-full"
              v-model="computedTargetLimits" />
          </div>
          <div class="flex flex-col">
            <TextField
              id="target-retention"
              label="Target Retention"
              type="text"
              class="w-full"
              v-model="computedTargetRetention" />
          </div>
        </div>
      </div>
      <div class="sticky top-20 flex flex-col gap-2 px-4">
        <AppButton
          secondary
          class="text-base-200 hover:opacity-100"
          data-testid="submit-button"
          @click.stop.prevent="handleSubmit">
          {{ props.isEditMode ? 'Save' : 'Create' }}
        </AppButton>
        <AppButton
          outline
          @click.stop.prevent="router.back()">
          Cancel
        </AppButton>
      </div>
    </form>
  </div>
</template>
